import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="pt-24 pb-20 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-2 gradient-text">Terms of Service</h1>
          <p className="text-sm text-slate-500 mb-10">Last updated: 31 May 2026</p>

          <div className="glass-card p-8 space-y-8 text-slate-300 leading-relaxed">
            <section>
              <h2 className="text-xl font-bold text-white mb-3">1. Acceptance</h2>
              <p>
                By accessing skills.strongly.ai (the "Site"), you agree to be bound by
                these Terms of Service. If you do not agree, do not use the Site. We
                may update these terms at any time; continued use means acceptance of
                the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">2. What the Site Does</h2>
              <p>
                The Site is an open directory of skills for AI coding agents. It also
                provides a free in-browser evaluator that produces a numeric "quality
                score" and a "security report" for SKILL.md files and GitHub
                repositories you submit or scan.
              </p>
              <p className="mt-3">
                The Site does not author, vet, audit, or endorse skills published in
                the directory. Submitters are solely responsible for the content,
                lawfulness, safety, and behavior of the skills they publish.
              </p>
            </section>

            <section className="border border-amber-500/30 bg-amber-500/5 rounded-xl p-5">
              <h2 className="text-xl font-bold text-white mb-3">
                3. NO WARRANTY ON SCORES OR "SAFE" CLASSIFICATIONS
              </h2>
              <p>
                THE QUALITY SCORE, SECURITY SCORE, RISK SEVERITY (INCLUDING "LOW",
                "MEDIUM", "HIGH", "CRITICAL"), AND ANY RECOMMENDATION (INCLUDING "SAFE
                TO INSTALL", "REVIEW BEFORE INSTALL", OR "DO NOT INSTALL") ARE PROVIDED
                ON AN "AS IS" AND "AS AVAILABLE" BASIS FOR INFORMATIONAL PURPOSES ONLY.
              </p>
              <p className="mt-3">
                A "LOW" risk score, a high quality score, or any other favorable signal
                <strong> does not certify, guarantee, or warrant</strong> that a skill
                is secure, functional, free of vulnerabilities, free of malicious code,
                free of bugs, compliant with any law or standard, or fit for any
                particular purpose.
              </p>
              <p className="mt-3">
                Scoring tools use heuristics, static analysis, and (optionally) machine
                learning models that <strong>can and do produce false negatives and
                false positives</strong>. You are solely responsible for independently
                reviewing, testing, and validating any skill before installing or
                executing it in any environment.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">4. Use at Your Own Risk</h2>
              <p>
                Skills may invoke external tools, network requests, file system
                operations, and code execution. You assume all risk associated with
                installing, executing, or relying on any skill discovered through the
                Site, the Site's evaluator, or any export, badge, or report it
                produces.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">5. Disclaimer of Warranties</h2>
              <p className="uppercase text-sm">
                The Site and all content, services, scores, reports, badges, and
                recommendations are provided "AS IS" and "AS AVAILABLE" without
                warranties of any kind, whether express, implied, statutory, or
                otherwise, including without limitation warranties of merchantability,
                fitness for a particular purpose, accuracy, non-infringement, security,
                or that the Site will be uninterrupted or error-free.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">6. Limitation of Liability</h2>
              <p className="uppercase text-sm">
                To the fullest extent permitted by applicable law, in no event shall
                Strongly.AI, its affiliates, officers, employees, contributors, or
                licensors be liable for any indirect, incidental, special,
                consequential, exemplary, or punitive damages, or any loss of profits,
                revenue, data, goodwill, or other intangible losses arising out of or
                related to: (a) your use of or inability to use the Site; (b) any
                skill discovered, evaluated, installed, or executed via the Site;
                (c) any score, severity, recommendation, badge, or report produced
                by the Site; or (d) unauthorized access to or alteration of your
                transmissions or data.
              </p>
              <p className="mt-3 uppercase text-sm">
                The aggregate liability of Strongly.AI for any claim arising out of
                or related to the Site or these terms shall not exceed one hundred
                US dollars (US$100).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">7. Indemnity</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Strongly.AI and its
                affiliates from any claim, loss, damage, liability, or expense
                (including reasonable attorneys' fees) arising out of (a) your use of
                the Site, (b) any skill you submit, install, or distribute, or
                (c) your violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">8. Submitted Content</h2>
              <p>
                When you submit a skill, you represent that you own or have permission
                to publish it and that it does not infringe any third-party rights.
                You grant the Site a worldwide, non-exclusive, royalty-free license to
                host, display, index, score, and distribute the metadata and content of
                the submission. You may remove your submission at any time by deleting
                the underlying GitHub repository or contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">9. Prohibited Use</h2>
              <p>You agree not to submit or distribute skills that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>contain malware, ransomware, spyware, or other harmful code;</li>
                <li>are designed to exfiltrate user data without consent;</li>
                <li>violate applicable laws or third-party rights;</li>
                <li>attempt to bypass platform safety measures.</li>
              </ul>
              <p className="mt-3">We may remove any submission at our discretion.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">10. Third-Party Services</h2>
              <p>
                The Site links to GitHub-hosted repositories and uses third-party
                services (CDN, hosting, optional model APIs). We are not responsible
                for the content, availability, or practices of any third-party service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">11. Governing Law</h2>
              <p>
                These terms are governed by the laws of the State of California,
                without regard to its conflict-of-laws principles. You agree to
                exclusive jurisdiction of the state and federal courts in San
                Francisco County, California.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">12. Contact</h2>
              <p>
                Questions about these terms: <a className="text-brand-300" href="mailto:support@strongly.ai">support@strongly.ai</a>
              </p>
            </section>

            <p className="text-xs text-slate-500 pt-4 border-t border-white/5">
              See also: <Link to="/privacy" className="text-brand-300 hover:text-brand-200">Privacy Policy</Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
